import { Invoice } from './Invoice'
import { AgencyReserveType } from './ReserveSettings'

export interface OffAdCritterCustomOption {
  name: string
  color: string
  order: number
}

export interface Organization {
  id: string
  name: string
  billingEmail: string
  phoneNumber: string
  targetingTemplates: string[]
  hasPaymentMethod: boolean
  hasCanceledSubscription: boolean
  hasUnpaidSubscription: boolean
  includedCampaignIds: string[] | null
  group: string | null
  signupMethod: string
  campaignAdvisorId: string
  isAgency: boolean
  hasBundle: boolean
  subscriptionId: string
  allowBankAccountPayments: boolean
  permissions?: Permission[]
  defaultTargetingId?: string
  whiteLabel: WhiteLabel
  hasSamplePitchDeck: boolean
  organizationReserveSettings?: OrganizationReserveSettings
  aiPlanMinimumBudget: number
}

export interface OrganizationReserveSettings {
  reserveRate: number
  agencyReserveType: AgencyReserveType
}

export enum WhiteLabel {
  AdCritter = 'AdCritter',
  Agency = 'Agency',
  Vista = 'Vista',
  AgencyV2 = 'AgencyV2',
  Enterprise = 'Enterprise'
}

export interface Permission {
  permissionType: PermissionType
}

export enum PermissionType {
  UploadAudience = 'UploadAudience',
  UseTemplates = 'UseTemplates',
  UseTemplateBuilderCampaignCreation = 'UseTemplateBuilderCampaignCreation',
  ShowCustomizablePixel = 'ShowCustomizablePixel',
  AgencyReserve = 'AgencyReserve',
  HideAgencyReserve = 'HideAgencyReserve',
  HideLegacyPlanner = 'HideLegacyPlanner',
  PlatformCampaignApprovalProcess = 'PlatformCampaignApprovalProcess'
}

export interface PaymentMethod {
  id: string
  name: string
  nickname?: string
  brand: string
  last4: string
  expiryMonth: number
  expiryYear: number
  isDefault: boolean
  spendCap?: number
  customerId: string
  paymentMethodType: PaymentMethodType
  currentBalance?: number
  pendingCharges?: number
  availableBalance?: number
}

export enum PaymentMethodType {
  Card = 'Card',
  BankAccount = 'BankAccount',
  Balance = 'Balance',
  Token = 'Token'
}

export const displayPaymentMethod = (paymentMethod?: PaymentMethod) => {
  if (!paymentMethod) {
    return 'N/A'
  }

  return `${paymentMethod.brand} card ending ${displayPaymentMethodShort(paymentMethod)}`
}

export const displayPaymentMethodShort = (paymentMethod?: PaymentMethod) => {
  if (!paymentMethod) {
    return 'N/A'
  }

  return `xxxx${paymentMethod.last4}, expiring ${paymentMethod.expiryMonth.toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false
  })}/${paymentMethod.expiryYear}`
}

export interface PaymentMethodSetup {
  clientSecret: string
}

export interface PaymentMethodPayment {
  clientSecret: string
  paymentIntentId: string
}

export interface Subscription {
  level: string
  status: string
  nextBillDate: string
  nextBillAmount: number
  canceledDate: string
  cancelAtPeriodEnd: boolean
  endDate?: string
  invoices: Invoice[]
}

export interface OrganizationPrepareRestart {
  paymentIntentId: string
  clientSecret: string
  paymentMethodId: string | null
  amount: number
  hasValidSubscription: boolean
}

export interface OrganizationPrepareUnpaid {
  isStillUnpaid: boolean
  clientSecret: string
  invoiceId: string | null
  paymentMethodId: string
  amount: number
  paymentMethods: PaymentMethod[]
  error: string
  setupIntentId: string
}

export interface OrganizationPrepareCapture {
  clientSecret: string
  setupIntentId: string
  paymentMethodId: string
  redirectUrl: string
  amount: number
  error: string
  remainingBalance?: number
  cappedAmount?: number
  needsConfirmation: boolean
}

export interface OrganizationPrepareCaptureResult {
  success: boolean
  error: string
}
